import { MapsWidget } from '@/components/MapsWidget';
export const frontmatter = {
  category: 'atracao',
  title: 'Fortaleza de Nossa Senhora dos Prazeres : Histoire et comment visiter (2026)',
  excerpt: 'Fortaleza de Nossa Senhora dos Prazeres sur Ilha do Mel : le seul monument militaire du XVIIIe siècle du Paraná. Histoire, canons, accès par sentier ou bateau et conseils de visite.',
  publishedAt: '2026-04-25',
  author: 'Guia Ilha do Mel',
  tags: ['atracoes', 'fortaleza', 'historia', 'patrimonio', 'nova-brasilia'],
  coverImage: '/images/fortaleza.jpg',
  readingTime: '7 min',
};

# Fortaleza de Nossa Senhora dos Prazeres : Histoire et comment visiter

La **forteresse Notre-Dame des Plaisirs** est le **seul monument militaire du XVIIIe siècle du Paraná** et l'un des sites historiques les plus importants de la côte sud du Brésil. Construite entre 1767 et 1769 pour protéger la baie de Paranaguá des invasions, elle est aujourd'hui classée au patrimoine historique et artistique national et figure parmi les incontournables de l'Ilha do Mel (Île au Miel).

Si vous recherchez des informations sur « la forteresse d'Ilha do Mel », « la forteresse de Nossa Senhora dos Prazeres » ou « l'histoire d'Ilha do Mel », ce guide contient tout ce qu'il vous faut : l'histoire complète, comment s'y rendre, ce qu'il faut voir et des conseils que seuls les locaux connaissent.

---

Histoire de la forteresse

Pourquoi a-t-il été construit ?

Au XVIIIe siècle, la baie de Paranaguá était l'une des routes commerciales les plus importantes du Brésil colonial. L'or, le maté et le bois étaient expédiés du port de Paranaguá vers l'Europe, attirant les pirates et l'attention des couronnes espagnole et anglaise.

Pour protéger cette richesse, le gouvernement portugais a construit la forteresse de Nossa Senhora dos Prazeres entre **1767 et 1769**, stratégiquement positionnée au point culminant de l'Ilha do Mel, dominant l'entrée de la baie.

Spécifications techniques

| Caractéristique | Valeur |

| -------------- | ----------------------------------------- |

| **Construction** | 1767–1769 |

| **Fonction** | Protection de la baie de Paranaguá |

| **Murs** | 1,5 mètre d'épaisseur |

| **Canons** | Anglais et portugais (XVIIIe et XIXe siècles) |

| **Statut** | Patrimoine historique et artistique national |

| **Note** | 4,6/5 (720 avis) |

---

Que voir à Fortaleza ?

1. Labyrinthe de canons

Au sommet de la colline, un système de tranchées et de plates-formes forme le **Labyrinthe des canons** — un réseau de passages entre les positions d'artillerie qui offre une vue panoramique à 360° sur la baie.

2. Canons d'origine

La forteresse conserve des canons anglais et portugais des XVIIIe et XIXe siècles, dont certains reposent encore sur leurs socles d'origine. Les inscriptions gravées sur les canons racontent l'histoire de chaque pièce.

3. Les tranchées de la Seconde Guerre mondiale

Durant la Seconde Guerre mondiale, l'île de Mel fut de nouveau militarisée. Les tranchées creusées à cette époque existent toujours et peuvent être visitées.

4. Centre d'accueil des visiteurs

Le centre d'accueil des visiteurs propose des expositions sur l'histoire de la forteresse, de l'île et de la région de Paranaguá. Il est ouvert de 9 h à 17 h.

5. Point de vue panoramique

Le point de vue situé au sommet de la forteresse offre l'une des vues les plus impressionnantes sur Ilha do Mel — la baie de Paranaguá, le continent et les îles voisines dans un panorama à 360°.

---

Comment se rendre à la forteresse

Option 1 : Sentier (option la moins chère)

Au départ de Nova Brasília, le sentier menant à Fortaleza est l'un des plus plats de l'île, pratiquement sans aucune montée.

- **Distance :** 3,7 km (7,4 km aller-retour)

- **Durée :** 2 heures (aller-retour)

- **Difficulté :** Facile

- **Possibilité de porter des tongs :** Oui

**Conseil :** Le sentier côtier (4 km) est plus beau que le sentier intérieur, mais il n’est accessible qu’à marée basse. À marée haute, empruntez le sentier intérieur.

Option 2 : Bateau (plus confortable)

Les bateaux partent de Nova Brasília directement vers Fortaleza.

- **Durée :** 10 minutes

- **Prix :** 15 à 25 R$

- **Avantage :** Vous arrivez reposé(e) et vous pouvez rentrer en bateau.

[Guide complet des sentiers d'Ilha do Mel →](/blog/trilhas-ilha-do-mel)

---

Conseils de visite

| Conseil | Pourquoi |

| -------------------------------- | ----------------------------------------------------- |

| Visitez le matin | Le centre d'accueil ferme à 17h |

| Combinez avec la plage de Fortaleza | 4 km de plage déserte se trouvent juste à côté |

| Apportez de l'eau | Il n'y a pas de vendeurs le long du chemin |

| Crème solaire et chapeau | Peu d'ombre sur le sentier et à la forteresse |

| Prévoyez la journée entière | 2 heures de randonnée + 1 heure à la forteresse + plage = journée complète |

---

Plage de Fortaleza

À proximité de la forteresse se trouve **Fortress Beach**, une plage quasi déserte de 4 km. C'est l'une des plages les plus isolées de l'île, idéale pour ceux qui recherchent le calme après une visite historique.

- **Infrastructures :** nulles

- **Circulation :** très faible

- **Idéal pour :** se détendre après la visite de la forteresse

---

Foire aux questions

**L'entrée à la forteresse est-elle payante ?**

Non, l'entrée est gratuite.

**Est-ce adapté aux enfants ?**

Oui. Le sentier est plat et la forteresse dispose d'un espace ouvert et sécurisé.

**Avez-vous besoin d'un guide ?**

Ce n'est pas obligatoire, mais les guides locaux proposent des visites guidées avec des anecdotes qui ne figurent pas sur les panneaux.

Quelle est la meilleure période pour y aller ?

Toute l’année. Mais à marée basse, on peut se promener le long de la plage, ce qui est encore plus beau.

<MapsWidget query="Fortaleza de Nossa Senhora dos Prazeres Ilha do Mel" />

---

Continuez à explorer

La forteresse n'est qu'une des 7 attractions de l'Ilha do Mel. Découvrez les autres dans le [guide complet des attractions](/what-to-do) et trouvez les [meilleures plages](/blog/best-beaches) pour compléter votre itinéraire.

Vous souhaitez planifier l'intégralité de votre voyage ? Notre [guide complet de l'Ilha do Mel](/guide-complete) propose des options adaptées à votre budget, des conseils de locaux et tout ce dont vous avez besoin.